import { IAddressSchemaProperties } from '../api/JsonSchemaApi';
import { IAddressDisplaySchema } from '../domain/entities/Config/Config';
import Address, { IAddressAttributes } from '../domain/entities/Address';
import CompanyBillingDetails from '../domain/entities/CompanyBillingDetails';
import { formatPhone, getCountryAbbr, trimCountryCode } from './AddressFormUtils';
import CONSTANTS, { FIELD_TYPE } from '../constants/Constants';
import { stringMask } from './StringMasking';
import { isNorthAmericaCountry, isTurkey } from './CountryUtils';
import { Country, phoneGetCountryCallingCode } from '@estee/elc-inputs';

export const getAddressDisplaySchema = (
  validationSchema: IAddressSchemaProperties,
): IAddressDisplaySchema => {
  const displaySchema: IAddressDisplaySchema = {};

  if (validationSchema) {
    (Object.keys(validationSchema) as Array<keyof typeof validationSchema>)
      .forEach((fieldName: keyof IAddressAttributes) => {
        if (validationSchema[fieldName].additionalProperties
          && validationSchema[fieldName].additionalProperties.display) {
          const displaySchemaName = fieldName === FIELD_TYPE.COUNTRY_ID ? FIELD_TYPE.COUNTRY : fieldName;
          displaySchema[displaySchemaName] = {
            ...validationSchema[fieldName].additionalProperties.display,
            asNational: !!validationSchema[fieldName]?.additionalProperties?.excludeCountryCode,
          };
        }
      });
  }

  return displaySchema;
};

// Util used to transform an address into the matrix accepted by AddressDisplay component
export const getAddressDisplayMatrix = (
  displaySchema: IAddressDisplaySchema,
  address: Address | CompanyBillingDetails,
): string[][] => {
  const matrix: string[][] = [];

  if (!displaySchema) {
    return matrix;
  }

  Object.keys(displaySchema).forEach((addressFieldKey: keyof IAddressAttributes) => {
    const { x, y, field_separator: fieldSeparator, asNational } = displaySchema[addressFieldKey];
    const addr = address as Address;
    
    let addressValue = addr.attributes[addressFieldKey] as string;

    if (addressFieldKey.includes(FIELD_TYPE.PHONE)) {
      addressValue = formatPhone(addressValue, addr.attributes[FIELD_TYPE.COUNTRY_ID], !!asNational);
    }

    const { mask } = displaySchema[addressFieldKey];
    if (addressValue && mask) {
      addressValue = stringMask(mask, addressValue);
    }

    if (!matrix[y - 1]) {
      matrix[y - 1] = [];
    }
    if (fieldSeparator) addressValue += fieldSeparator;
    matrix[y - 1][x - 1] = addressValue;
  });

  // Exclude undefined rows
  return matrix.filter(row => row?.length);
};

// TODO Define actual implementation of prefix handling across al brands and sites (RCO-3210)
export const removePrefixForPhone = (
  mobileNumber: string,
  countryId: number,
): string => {
  if (
    isNorthAmericaCountry(countryId)
      && mobileNumber.charAt(0) === CONSTANTS.PHONE_PREFIXES.NA
  ) {
    return mobileNumber.slice(1);
  }
  if (isTurkey(countryId) && mobileNumber.slice(0, 2) === CONSTANTS.PHONE_PREFIXES.TR) {
    return mobileNumber.slice(2);
  }
  const countriesToRemovePrefix :Country[] = ['MX', 'BR', 'CL'];
  const countryCode = getCountryAbbr(countryId).toUpperCase() as Country;
  if (countriesToRemovePrefix.includes(countryCode)) {
    const countryCallingCode = phoneGetCountryCallingCode(countryCode);
    return trimCountryCode(mobileNumber, countryCallingCode);
  }

  return mobileNumber;
};
